/**
 * EMP-WORKSPACE-001 — dedicated Employee Workspace.
 * Create mode  (/admin/hr/employees/new):        6-step wizard + Review step,
 *   progress indicator, per-step validation, auto-saved resumable drafts,
 *   real-time duplicate checks, redirect to the profile on creation.
 * Edit mode    (/admin/hr/employees/:id/edit):   section tabs
 *   Overview | Basic | Employment | Personal | Contact | Statutory | Banking | Activity
 * EMP-WS-UI-001 (Sep 2026): premium shell (hero, icon tabs, overview cards, timeline,
 *   quick-actions rail). Presentation only — endpoints, validation, RBAC unchanged.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../ui/button';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronLeft, Loader2,
  Save, ShieldAlert, LogOut, UserPlus, Pencil, Eye, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { WORKSPACE_STEPS, buildEmptyWorkspaceForm, validateStep } from './employeeWorkspaceConfig';
import EmployeeIdChangeDialog from './EmployeeIdChangeDialog';
import WorkEmailChangeDialog from './WorkEmailChangeDialog';
import ShiftScheduleSection from './ShiftScheduleSection';
import ProbationTab from './probation/ProbationTab';
import LinkAccountModal from './workspace/LinkAccountModal';
import { EmployeeHero, usePhotoActions } from './workspace/EmployeeHero';
import { WorkspaceTabs, TAB_META } from './workspace/WorkspaceTabs';
import { PremiumField, ReadOnlyField } from './workspace/PremiumField';
import { SectionCard, InfoTiles, AttendanceCard, LinkedAccountCard, LeaveBalancesCard } from './workspace/OverviewCards';
import { ActivityTimeline } from './workspace/ActivityTimeline';
import { QuickActionsCard, EmployeeStatusCard, QA_ICONS } from './workspace/QuickActionsRail';
import { getUserFromToken } from '../../../utils/adminApi';
import { isHrReadOnly } from '../../../lib/hrRole';
import { roleLabel } from './RoleSelect';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ProgressIndicator = ({ current }) => (
  <div className="flex items-center gap-1 flex-wrap" data-testid="ws-progress">
    {WORKSPACE_STEPS.map((s, i) => (
      <React.Fragment key={s.id}>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
          i < current ? 'bg-emerald-100 text-emerald-700'
            : i === current ? 'bg-slate-900 text-white'
            : 'bg-slate-100 text-slate-400'}`}>
          {i < current ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
          {s.title}
        </div>
        {i < WORKSPACE_STEPS.length - 1 && <div className="w-4 h-px bg-slate-200" />}
      </React.Fragment>
    ))}
    <div className="w-4 h-px bg-slate-200" />
    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
      current === WORKSPACE_STEPS.length ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}>
      Review
    </div>
  </div>
);

const ReviewSummary = ({ formData, options }) => (
  <div className="space-y-5" data-testid="ws-review-summary">
    {WORKSPACE_STEPS.map(step => {
      const filled = step.fields.filter(f => formData[f.key]);
      if (!filled.length) return null;
      const labelFor = (f, v) => {
        const opts = f.options ? f.options.map(o => ({ value: o, label: o })) : (options[f.optionsKey] || []);
        return opts.find(o => o.value === v)?.label || (f.type === 'role' ? roleLabel(v) : v);
      };
      return (
        <div key={step.id}>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1.5 mb-3 flex items-center gap-2">
            {step.title}{step.sensitive && <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />}
          </h4>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            {filled.map(f => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <span className="text-xs text-slate-400">{f.label}: </span>
                <span className="text-sm font-medium text-slate-700">{labelFor(f, formData[f.key])}</span>
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const SECTION_SUB = {
  basic: 'Identity, team and joining details', employment: 'Update employment information', personal: 'Personal details (restricted)',
  contact: 'Phone numbers, emergency contact and addresses', statutory: 'Statutory identifiers (restricted)', banking: 'Payroll bank account (restricted)',
};

export default function EmployeeWorkspace() {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!employeeId;

  const [formData, setFormData] = useState(buildEmptyWorkspaceForm());
  const [record, setRecord] = useState(null); // full employee doc (edit mode) — hero/overview/rail
  const [leaveBal, setLeaveBal] = useState(null);
  const [options, setOptions] = useState({ roles: [], teams: [], timezones: [], managers: [], statuses: [], locations: [] });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0); // create mode: 0..5 fields, 6 = review
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview'); // edit mode
  const [draftId, setDraftId] = useState(searchParams.get('draft') || null);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [loading, setLoading] = useState(true);
  const [dupWarnings, setDupWarnings] = useState({});
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [showIdChange, setShowIdChange] = useState(false);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // 'link' | 'unlink' | null
  const dupTimer = useRef(null);
  const me = getUserFromToken();
  const canEditEmployeeId = ['admin', 'hr_manager'].includes((me?.role || '').toLowerCase());
  const readOnly = isHrReadOnly();

  const applyRecord = useCallback((emp) => {
    setRecord(emp);
    const base = buildEmptyWorkspaceForm();
    const form = Object.fromEntries(Object.keys(base).map(k => [k, emp[k] ?? base[k] ?? '']));
    form.date_of_joining = emp.date_of_joining || ''; // P6 B1 — never pre-fill a missing DOJ (the create-form default is "today")
    setFormData(form);
  }, []);

  const reloadEmployee = useCallback(async () => {
    const headers = authHeaders();
    const emp = await axios.get(`${API}/api/hr/employees/${employeeId}?include_attendance=true`, { headers });
    applyRecord(emp.data);
    // EMP-USER-LINK-001 — same source as the profile page (explicit link first, email fallback)
    axios.get(`${API}/api/leave-registry/user-balance`, { headers, params: { employee_id: employeeId } })
      .then(res => setLeaveBal(res.data)).catch(() => setLeaveBal(null));
  }, [employeeId, applyRecord]);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = authHeaders();
        const keys = ['roles', 'teams', 'timezones', 'managers', 'statuses', 'locations'];
        const res = await Promise.all(keys.map(k => axios.get(`${API}/api/hr/options/${k}`, { headers })));
        setOptions(Object.fromEntries(keys.map((k, i) => [k, res[i].data[k] || []])));
        if (isEdit) {
          await reloadEmployee();
        } else if (searchParams.get('draft')) {
          const d = await axios.get(`${API}/api/hr/employee-drafts/${searchParams.get('draft')}`, { headers });
          setFormData({ ...buildEmptyWorkspaceForm(), ...(d.data.data || {}) });
          setStep(Math.min(d.data.current_step || 0, WORKSPACE_STEPS.length));
        }
      } catch {
        toast.error('Failed to load workspace data');
      } finally {
        setLoading(false);
      }
    };
    load();
    // options + record load once per employee/draft
  }, [employeeId]);

  // ── Real-time duplicate validation (debounced) ──
  const setField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
    if (key === 'work_email' || key === 'employee_id') {
      clearTimeout(dupTimer.current);
      dupTimer.current = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (key === 'work_email' && value.trim()) params.set('work_email', value.trim());
          if (key === 'employee_id' && value.trim()) params.set('employee_id', value.trim());
          if (isEdit) params.set('exclude_employee_id', employeeId);
          if (![...params.keys()].length) { setDupWarnings(p => ({ ...p, [key]: null })); return; }
          const res = await axios.get(`${API}/api/hr/employees/check-duplicate?${params}`, { headers: authHeaders() });
          const taken = key === 'work_email' ? res.data.work_email_taken
            : res.data.employee_id_taken;
          const owner = key === 'work_email' ? res.data.work_email_owner
            : res.data.employee_id_owner;
          setDupWarnings(p => ({ ...p, [key]: taken
            ? (key === 'employee_id'
                ? `This Employee ID already exists (${owner}). Please enter a different Employee ID.`
                : `Already used by ${owner}`)
            : null }));
        } catch { /* non-blocking */ }
      }, 500);
    }
  };

  // ── Drafts (create mode) ──
  const saveDraft = useCallback(async (silent = false) => {
    try {
      const res = await axios.post(`${API}/api/hr/employee-drafts`,
        { id: draftId, data: formData, current_step: step }, { headers: authHeaders() });
      if (!draftId) setDraftId(res.data.id);
      setLastSavedAt(new Date());
      if (!silent) toast.success('Draft saved');
      return true;
    } catch {
      if (!silent) toast.error('Failed to save draft');
      return false;
    }
  }, [draftId, formData, step]);

  const currentStepDef = WORKSPACE_STEPS[step];

  const goNext = async () => {
    const errs = validateStep(currentStepDef, formData);
    if (dupWarnings.work_email && currentStepDef.id === 'basic') errs.work_email = dupWarnings.work_email;
    if (dupWarnings.employee_id && currentStepDef.id === 'basic') errs.employee_id = dupWarnings.employee_id;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    await saveDraft(true); // auto-save between steps
    setStep(s => s + 1);
  };

  const goPrev = async () => { await saveDraft(true); setStep(s => Math.max(0, s - 1)); };

  const handleCreate = async () => {
    try {
      setBusy(true);
      const payload = Object.fromEntries(Object.entries(formData).filter(([, v]) => v !== ''));
      const res = await axios.post(`${API}/api/hr/employees`, payload, { headers: authHeaders() });
      if (draftId) {
        try { await axios.delete(`${API}/api/hr/employee-drafts/${draftId}`, { headers: authHeaders() }); } catch { /* noop */ }
      }
      const newId = res.data?.employee?.employee_id || res.data?.employee_id;
      toast.success(`Employee ${newId} created`);
      navigate(`/admin/hr/employees/${newId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create employee');
    } finally {
      setBusy(false);
    }
  };

  // Ab (user choice): stay on the Edit page, Saving… → Saved ✓, refresh from the existing source, keep the tab.
  const handleUpdate = async () => {
    const roleErrs = validateStep(WORKSPACE_STEPS[0], formData);
    if (roleErrs.role) { setErrors(prev => ({ ...prev, role: roleErrs.role })); toast.error(roleErrs.role); return; }
    try {
      setBusy(true); setSaveState('saving');
      const payload = { ...formData };
      if (!payload.role) payload.role = null; // optional job function — blank clears / stays unset
      delete payload.work_email;      // immutable
      // PROBATION-REVIEW-001 P6 B1 — DOJ is immutable once stored; it may only be SET when the record has none.
      if (record?.date_of_joining || !payload.date_of_joining) delete payload.date_of_joining;
      await axios.put(`${API}/api/hr/employees/${employeeId}`, payload, { headers: authHeaders() });
      await reloadEmployee();
      if (activeTab === 'activity') loadAudit();
      toast.success('Employee updated');
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1600);
    } catch (err) {
      setSaveState('idle');
      toast.error(err.response?.data?.detail || 'Failed to update employee');
    } finally {
      setBusy(false);
    }
  };

  const loadAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/hr/audit-log?employee_id=${employeeId}&limit=25`, { headers: authHeaders() });
      setAuditRows(res.data.data || res.data.logs || []);
    } catch { /* noop */ }
  }, [employeeId]);
  useEffect(() => { if (isEdit && activeTab === 'activity') loadAudit(); }, [isEdit, activeTab, loadAudit]);

  const photo = usePhotoActions(employeeId, (p) => setRecord(prev => ({ ...prev, ...p })));

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const editTabs = ['overview', ...WORKSPACE_STEPS.map(s => s.id), 'probation', 'activity']; // PROBATION-REVIEW-001 P2
  const stepFieldsFor = (id) => WORKSPACE_STEPS.find(s => s.id === id);
  const isFormTab = isEdit && activeTab !== 'overview' && activeTab !== 'activity' && activeTab !== 'probation';
  const hero = record || {}; // saved truth only — form defaults must not leak into the header/rail

  const quickActions = [
    ...(canEditEmployeeId ? [
      { icon: QA_ICONS.id, label: 'Edit Employee ID', onClick: () => setShowIdChange(true), testid: 'qa-edit-employee-id' },
      { icon: QA_ICONS.email, label: 'Edit Work Email', onClick: () => setShowEmailChange(true), testid: 'qa-edit-work-email' },
    ] : []),
    ...(!readOnly ? [{ icon: QA_ICONS.link, label: record?.linked_user_id ? 'Change / Unlink User Account' : 'Link User Account', onClick: () => setLinkModal('link'), testid: 'qa-link-user' }] : []),
    { icon: QA_ICONS.adjustments, label: 'Adjustment History', disabled: !leaveBal?.user_id, hint: leaveBal?.user_id ? undefined : 'Available once a user account is linked',
      onClick: () => navigate(`/admin/workspace?tab=leave&subtab=adjustments&user_id=${leaveBal.user_id}`), testid: 'qa-adjustment-history' },
    { icon: QA_ICONS.audit, label: 'View Audit History', onClick: () => setActiveTab('activity'), testid: 'qa-view-audit' },
  ];

  // HR-PERSONA-001: hr_specialist is read-only — never render Save (backend enforces 403 regardless)
  const saveButton = (testid = 'ws-update-btn') => readOnly ? null : (
    <Button onClick={handleUpdate} disabled={busy} className="an-btn an-btn-primary gap-2 h-10 rounded-xl px-4" data-testid={testid}>
      {saveState === 'saving' ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
        : saveState === 'saved' ? <span className="ews-saved inline-flex items-center gap-2" data-testid="ws-saved-state"><CheckCircle2 className="w-4 h-4" /> Saved ✓</span>
        : <><Save className="w-4 h-4" /> Save Changes</>}
    </Button>
  );

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto space-y-4" data-testid="employee-workspace">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[13px] text-slate-500">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/hr/employees')} className="an-btn h-8 rounded-full px-2.5" data-testid="ws-back-btn">
            <ChevronLeft className="w-4 h-4" /> Directory
          </Button>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-slate-800">{isEdit ? 'Edit Employee' : 'New Employee'}</span>
          {!isEdit && lastSavedAt && (
            // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local autosave clock
            <span className="text-xs text-slate-400 ml-2">Draft auto-saved {lastSavedAt.toLocaleTimeString()}</span>
          )}
        </div>
        {!isEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => saveDraft(false)} className="gap-2" data-testid="ws-save-draft-btn">
              <Save className="w-4 h-4" /> Save Draft
            </Button>
            <Button variant="outline" className="gap-2" data-testid="ws-save-exit-btn"
              onClick={async () => { if (await saveDraft(true)) { toast.success('Draft saved'); navigate('/admin/hr/employees'); } }}>
              <LogOut className="w-4 h-4" /> Save & Exit
            </Button>
          </div>
        )}
        {isEdit && <p className="hidden md:block an-accent text-[18px] text-violet-500/90">People Make Progress</p>}
      </div>

      {/* CREATE MODE — wizard */}
      {!isEdit && (
        <div className="lv-card">
          <div className="px-6 py-4 border-b border-slate-100">
            <ProgressIndicator current={step} />
          </div>
          <div className="p-6">
            {step < WORKSPACE_STEPS.length ? (
              <>
                <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  Step {step + 1} — {currentStepDef.title}
                  {currentStepDef.sensitive && <ShieldAlert className="w-4 h-4 text-amber-500" />}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {currentStepDef.fields.map(f => (
                    <PremiumField key={f.key} field={f} value={formData[f.key]}
                      error={errors[f.key] || (f.dupCheck && dupWarnings[f.key])}
                      onChange={setField} options={options} />
                  ))}
                </div>
              </>
            ) : (
              <>
                <h3 className="text-base font-semibold text-slate-800 mb-4">Review & Confirm</h3>
                <ReviewSummary formData={formData} options={options} />
              </>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
            <Button variant="outline" onClick={goPrev} disabled={step === 0 || busy} className="gap-2" data-testid="ws-prev-btn">
              <ArrowLeft className="w-4 h-4" /> Previous
            </Button>
            {step < WORKSPACE_STEPS.length ? (
              <Button onClick={goNext} disabled={busy} className="gap-2" data-testid="ws-next-btn">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleCreate} disabled={busy} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="ws-create-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Create Employee
              </Button>
            )}
          </div>
        </div>
      )}

      {/* EDIT MODE — premium workspace */}
      {isEdit && record && (
        <>
          <EmployeeHero employee={hero} readOnly={readOnly} onPhotoUpload={photo.upload} onPhotoRemove={photo.remove}>
            <Button variant="outline" onClick={() => navigate(`/admin/hr/employees/${employeeId}`)} className="an-btn h-10 rounded-xl gap-2" data-testid="ws-view-profile-btn">
              <Eye className="w-4 h-4" /> View Profile
            </Button>
            {isFormTab && saveButton('ws-update-btn-header')}
          </EmployeeHero>

          <WorkspaceTabs tabs={editTabs} active={activeTab} onChange={setActiveTab} />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
            <div key={activeTab} className="ews-panel space-y-4 min-w-0">
              {activeTab === 'overview' && (
                <>
                  <SectionCard icon={TAB_META.overview.icon} title="Employee Overview" subtitle="Key information at a glance" testid="ws-overview" delay={120}>
                    <InfoTiles employee={hero} />
                  </SectionCard>
                  <AttendanceCard att={record.attendance_summary} delay={180} />
                  <LinkedAccountCard employee={record} readOnly={readOnly} onLink={() => setLinkModal('link')} onUnlink={() => setLinkModal('unlink')} delay={220} />
                  <LeaveBalancesCard leaveBal={leaveBal} onHistory={() => navigate(`/admin/workspace?tab=leave&subtab=adjustments&user_id=${leaveBal.user_id}`)} delay={260} />
                </>
              )}
              {activeTab === 'probation' && <ProbationTab employeeId={employeeId} readOnly={readOnly} onChanged={reloadEmployee} />}
              {activeTab === 'activity' && (
                <SectionCard icon={TAB_META.activity.icon} title="Activity History" subtitle="Most recent audited changes to this employee record" testid="ws-activity" delay={120}
                  action={<Link to={`/admin/hr/audit-log?employee_id=${encodeURIComponent(employeeId)}`} className="an-btn inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-violet-200 bg-white text-[12.5px] font-semibold text-violet-700 hover:bg-violet-50" data-testid="ws-view-full-audit">View Full Audit History <ArrowRight className="h-3.5 w-3.5" /></Link>}>
                  <ActivityTimeline rows={auditRows} testid="ws-activity-list" />
                </SectionCard>
              )}
              {isFormTab && (
                <SectionCard icon={TAB_META[activeTab].icon} title={`${TAB_META[activeTab].label} Details`} subtitle={SECTION_SUB[activeTab]} testid={`ws-section-${activeTab}`} delay={120}
                  action={stepFieldsFor(activeTab)?.sensitive && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[11.5px] font-semibold text-amber-700" data-testid="ws-sensitive-badge"><ShieldAlert className="h-3.5 w-3.5" /> Sensitive</span>}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {stepFieldsFor(activeTab)?.fields.map(f => {
                      // EMP-MANUAL-ID-001 — employee_id is immutable after creation.
                      // P6 B1 — DOJ is editable only while the stored value is empty (Admin / HR Manager).
                      const dojSettable = f.key === 'date_of_joining' && !record?.date_of_joining && canEditEmployeeId;
                      const immutable = f.key === 'work_email' || (f.key === 'date_of_joining' && !dojSettable) || f.key === 'employee_id';
                      if (dojSettable) {
                        return (
                          <div key={f.key} data-testid="ws-doj-settable">
                            <PremiumField field={f} value={formData[f.key]} error={errors[f.key]} onChange={setField} options={options} />
                            <p className="mt-1 text-[11px] text-amber-700" data-testid="ws-doj-settable-hint">No date of joining on record — enter it once; it becomes immutable after saving (audited).</p>
                          </div>
                        );
                      }
                      // SHIFT-ATTENDANCE-001 — timezone is unified with Attendance (edit under Employment → Shift & Attendance)
                      if (f.key === 'employee_timezone') {
                        return <ReadOnlyField key={f.key} field={f} value={formData[f.key] || 'Inherited (team / company default)'} hint="Unified with Attendance — managed under Employment → Shift & Attendance" />;
                      }
                      if (!immutable) {
                        return <PremiumField key={f.key} field={f} value={formData[f.key]} error={errors[f.key] || (f.dupCheck && dupWarnings[f.key])} onChange={setField} options={options} />;
                      }
                      const pencil = (label, onClick, testid) => (
                        <Button type="button" variant="outline" size="sm" className="an-btn h-10 gap-1.5 shrink-0 rounded-lg border-violet-200 text-violet-700 hover:bg-violet-50" onClick={onClick} data-testid={testid}>
                          <Pencil className="w-3.5 h-3.5" /> {label}
                        </Button>
                      );
                      if (f.key === 'employee_id' && canEditEmployeeId) {
                        return <ReadOnlyField key={f.key} field={f} value={formData[f.key]} hint="Changing requires a reason and is audited" action={pencil('Edit Employee ID', () => setShowIdChange(true), 'edit-employee-id-btn')} />;
                      }
                      if (f.key === 'work_email' && canEditEmployeeId) {
                        return <ReadOnlyField key={f.key} field={f} value={formData[f.key]} hint="Changing requires a reason and is audited · login is not affected" action={pencil('Edit Work Email', () => setShowEmailChange(true), 'edit-work-email-btn')} />;
                      }
                      return <ReadOnlyField key={f.key} field={f} value={formData[f.key]} hint="Cannot be changed" />;
                    })}
                  </div>
                  {activeTab === 'employment' && (
                    <div className="mt-6 pt-5 border-t border-slate-100" data-testid="ws-shift-wrap">
                      <ShiftScheduleSection employeeId={employeeId} onSaved={reloadEmployee} />
                    </div>
                  )}
                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-[12px] text-slate-400 inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Changes are audited and applied immediately after saving.</p>
                    {saveButton()}
                  </div>
                </SectionCard>
              )}
            </div>
            <aside className="space-y-4 min-w-0">
              <QuickActionsCard actions={quickActions} />
              <EmployeeStatusCard employee={hero} />
            </aside>
          </div>
        </>
      )}

      {isEdit && (
        <WorkEmailChangeDialog open={showEmailChange}
          employee={{ employee_id: employeeId, full_name: formData.full_name, work_email: formData.work_email }}
          onClose={() => setShowEmailChange(false)}
          onChanged={(newEmail) => {
            setShowEmailChange(false);
            setFormData(prev => ({ ...prev, work_email: newEmail }));
            setRecord(prev => ({ ...prev, work_email: newEmail }));
          }} />
      )}
      {isEdit && (
        <EmployeeIdChangeDialog open={showIdChange}
          employee={{ employee_id: employeeId, full_name: formData.full_name }}
          onClose={() => setShowIdChange(false)}
          onChanged={(newId) => {
            setShowIdChange(false);
            navigate(`/admin/hr/employees/${newId}/edit`, { replace: true });
          }} />
      )}
      {isEdit && linkModal && record && (
        <LinkAccountModal mode={linkModal} employee={record} onClose={() => setLinkModal(null)}
          onDone={() => { setLinkModal(null); reloadEmployee(); }} />
      )}
    </div>
  );
}
